"use client";

import { ReactNode, useState } from "react";
import Link from "next/link";
import CVADrawer from "components/modals/common/CVADrawer";
import CVAModal from "components/modals/common/CVAModal";
import { buildClientPreviewUrl } from "hooks/previewMode";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
const LinkIcon = ({
  className = ""
}: {
  className?: string;
}) => <svg width='12' height='14' viewBox='0 0 12 14' fill='none' xmlns='http://www.w3.org/2000/svg' className={className} aria-hidden='true' data-sentry-element="svg" data-sentry-component="LinkIcon" data-sentry-source-file="ShareWithClientModal.tsx">
    <path d='M7.25 4.5H8.5C10.0188 4.5 11.25 5.73122 11.25 7.25C11.25 8.76878 10.0188 10 8.5 10H7.25M4.75 10H3.5C1.98122 10 0.75 8.76878 0.75 7.25C0.75 5.73122 1.98122 4.5 3.5 4.5H4.75M3.75 7.25H8.25' stroke='currentColor' strokeWidth='1.2' strokeLinecap='round' strokeLinejoin='round' data-sentry-element="path" data-sentry-source-file="ShareWithClientModal.tsx" />
  </svg>;
const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  }
};
const ShareSection = ({
  title,
  description,
  url,
  previewUrl
}: {
  title: string;
  description: ReactNode;
  url: string;
  previewUrl?: string;
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const handleCopy = async () => {
    if (!url) return;
    await copyToClipboard(url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };
  const previewHref = previewUrl || url;
  return <div className='flex w-full flex-col gap-4' data-sentry-component="ShareSection" data-sentry-source-file="ShareWithClientModal.tsx">
      <div className='flex w-full flex-col gap-1'>
        <div className='flex w-full items-center justify-between gap-2'>
          <p className='text-sm font-semibold tracking-[-0.14px] text-[#26262b]'>
            {title}
          </p>
          {previewHref ? <Link href={previewHref} target='_blank' rel='noopener noreferrer' prefetch={false} className='text-xs font-medium tracking-[-0.12px] text-[#866e3f] underline'>
              Preview
            </Link> : null}
        </div>
        <p className='text-xs font-medium leading-[1.3] tracking-[-0.12px] text-[#53535d]'>
          {description}
        </p>
      </div>
      <button type='button' onClick={() => void handleCopy()} className='flex w-full items-center justify-center gap-1.5 rounded-full border border-[#bcc1d6] bg-white px-3 py-2 text-xs font-medium text-[#53535d] transition-colors hover:bg-[#f8f9fc]'>
        <LinkIcon data-sentry-element="LinkIcon" data-sentry-source-file="ShareWithClientModal.tsx" />
        {isCopied ? "Copied!" : "Copy link"}
      </button>
    </div>;
};
const ShareContent = ({
  referralUrl,
  whiteLabelUrl
}: {
  referralUrl: string;
  whiteLabelUrl: string;
}) => <div className='flex w-full flex-col gap-6' data-sentry-component="ShareContent" data-sentry-source-file="ShareWithClientModal.tsx">
    <div className='flex w-full flex-col gap-2'>
      <h3 className='text-lg font-bold leading-[1.5] tracking-[-0.27px] text-[#222]'>
        Share this property
      </h3>
      <div className='rounded-lg bg-[rgba(244,242,233,0.5)] p-2'>
        <p className='text-xs font-medium leading-[1.6] tracking-[-0.12px] text-[#53535d]'>
          Your client will <span className='font-bold'>never see</span> your
          commission details or travel agent functionalities when you share a
          link
        </p>
      </div>
    </div>

    <ShareSection title='Referral link' description={<>
          This link allows you or your client to book the property. Includes the
          total price and your referral code
        </>} url={referralUrl} previewUrl={buildClientPreviewUrl(referralUrl)} data-sentry-element="ShareSection" data-sentry-source-file="ShareWithClientModal.tsx" />

    <div className='h-px w-full bg-[#e1e3ee]' />

    <ShareSection title='White label link' description='No branding or pricing details shown (not bookable by client)' url={whiteLabelUrl} data-sentry-element="ShareSection" data-sentry-source-file="ShareWithClientModal.tsx" />
  </div>;
const ShareWithClientModal = ({
  isOpen,
  onClose,
  referralUrl,
  whiteLabelUrl
}: {
  isOpen: boolean;
  onClose: () => void;
  referralUrl: string;
  whiteLabelUrl: string;
}) => {
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const content = <ShareContent referralUrl={referralUrl} whiteLabelUrl={whiteLabelUrl} />;
  if (isMobile) {
    return <CVADrawer isOpen={isOpen} onClose={onClose} intent='bottom' size='medium'>
        <div className='px-4 pb-6 pt-10'>{content}</div>
      </CVADrawer>;
  }
  return <CVAModal isOpen={isOpen} onClose={onClose} size='extraSmall' intent='modal' className='relative' data-sentry-element="CVAModal" data-sentry-component="ShareWithClientModal" data-sentry-source-file="ShareWithClientModal.tsx">
      <div className='px-10 pb-10 pt-12'>{content}</div>
    </CVAModal>;
};
export default ShareWithClientModal;